import React, { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, Download, Calendar, Users, RefreshCw, 
  FileText, DollarSign, UserCheck, Clock, Briefcase, GraduationCap,
  PieChart, Activity, Filter, ChevronDown
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import hrService from '../../services/hrService';

const ReportsAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  
  const [data, setData] = useState({
    employees: { total: 0, active: 0, onLeave: 0, newHires: 0 },
    attendance: { present: 0, absent: 0, late: 0, avgHours: 0 },
    leave: { pending: 0, approved: 0, rejected: 0, utilizationRate: 0 },
    payroll: { totalPaid: 0, avgSalary: 0, pendingPayments: 0 },
    recruitment: { openPositions: 0, applications: 0, interviews: 0, hires: 0 },
    training: { activeCourses: 0, enrollments: 0, completionRate: 0 },
    performance: { avgRating: 0, reviewsCompleted: 0, pendingReviews: 0 },
    departments: []
  });

  useEffect(() => {
    loadAllData();
  }, [dateRange]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const [employeesRes, departmentsRes, attendanceRes, attendanceTodayRes, leaveRes, recruitmentRes] = await Promise.allSettled([
        hrService.getEmployees({ limit: 500 }),
        hrService.getDepartments(),
        hrService.getAttendanceAnalytics({ date_from: dateRange.from, date_to: dateRange.to }),
        hrService.getAttendance ? hrService.getAttendance({ date_from: today, date_to: today, limit: 500 }) : Promise.resolve({ data: {} }),
        hrService.getLeaveAnalytics({ date_from: dateRange.from, date_to: dateRange.to }),
        hrService.getRecruitmentAnalytics({ date_from: dateRange.from, date_to: dateRange.to })
      ]);

      // Process employees data
      const empResult = employeesRes.status === 'fulfilled' ? employeesRes.value?.data : {};
      const empData = empResult?.data || empResult || {};
      const employees = empData.employees || [];
      const totalEmployees = empData.pagination?.total || employees.length || 0;
      const activeEmployees = employees.filter(e => e.status === 'active').length || totalEmployees;

      // Process departments
      const deptResult = departmentsRes.status === 'fulfilled' ? departmentsRes.value?.data : {};
      const deptData = deptResult?.data || deptResult || {};
      const departments = deptData.departments || [];
      
      // Count employees per department
      const deptCounts = departments.map(dept => ({
        name: dept.name,
        count: employees.filter(e => e.department_id === dept.id).length
      })).filter(d => d.count > 0);

      // Process today's attendance records
      const attTodayResult = attendanceTodayRes.status === 'fulfilled' ? attendanceTodayRes.value?.data : {};
      const attTodayData = attTodayResult?.data || attTodayResult || {};
      const todayRecords = attTodayData.attendance || attTodayData.records || [];
      
      // Count today's attendance by status
      let presentCount = 0, absentCount = 0, lateCount = 0;
      todayRecords.forEach(record => {
        if (record.status === 'present') presentCount++;
        else if (record.status === 'absent') absentCount++;
        else if (record.status === 'late') lateCount++;
      });
      
      // If no records, use analytics data
      if (todayRecords.length === 0) {
        const attResult = attendanceRes.status === 'fulfilled' ? attendanceRes.value?.data : {};
        const attData = attResult?.data || attResult || {};
        const statusDist = attData.statusDistribution || [];
        statusDist.forEach(item => {
          const count = parseInt(item.count) || 0;
          if (item.status === 'present') presentCount = count;
          else if (item.status === 'absent') absentCount = count;
          else if (item.status === 'late') lateCount = count;
        });
      }
      
      // Calculate avg hours
      const totalHours = todayRecords.reduce((sum, r) => sum + (parseFloat(r.total_hours) || 0), 0);
      const avgHours = todayRecords.length > 0 ? (totalHours / todayRecords.length).toFixed(1) : 8;

      // Process leave
      const leaveResult = leaveRes.status === 'fulfilled' ? leaveRes.value?.data : {};
      const leaveData = leaveResult?.data || leaveResult || {};

      // Process recruitment
      const recResult = recruitmentRes.status === 'fulfilled' ? recruitmentRes.value?.data : {};
      const recData = recResult?.data || recResult || {};

      setData({
        employees: {
          total: totalEmployees,
          active: activeEmployees,
          onLeave: leaveData.onLeaveCount || leaveData.currentlyOnLeave || 0,
          newHires: employees.filter(e => {
            const hireDate = new Date(e.hire_date);
            const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
            return hireDate >= thirtyDaysAgo;
          }).length
        },
        attendance: {
          present: presentCount || todayRecords.length,
          absent: absentCount,
          late: lateCount,
          avgHours: avgHours
        },
        leave: {
          pending: leaveData.pendingCount || leaveData.pending || leaveData.pendingRequests || 0,
          approved: leaveData.approvedCount || leaveData.approved || leaveData.approvedRequests || 0,
          rejected: leaveData.rejectedCount || leaveData.rejected || 0,
          utilizationRate: leaveData.utilizationRate || 0
        },
        payroll: {
          totalPaid: 0,
          avgSalary: 0,
          pendingPayments: 0
        },
        recruitment: {
          openPositions: recData.activeJobPostings || recData.openPositions || recData.totalJobPostings || 0,
          applications: recData.totalApplications || 0,
          interviews: recData.totalInterviews || 0,
          hires: recData.acceptedOffers || recData.totalHires || 0
        },
        training: {
          activeCourses: 0,
          enrollments: 0,
          completionRate: 0
        },
        performance: {
          avgRating: 0,
          reviewsCompleted: 0,
          pendingReviews: 0
        },
        departments: deptCounts.length > 0 ? deptCounts : departments.map(d => ({ name: d.name, count: 0 }))
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load some analytics data');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = (type) => {
    const timestamp = new Date().toISOString().split('T')[0];
    let csvContent = '';
    
    switch(type) {
      case 'summary':
        csvContent = `HR Summary Report\nGenerated: ${timestamp}\nDate Range: ${dateRange.from} to ${dateRange.to}\n\n`;
        csvContent += `Category,Metric,Value\n`;
        csvContent += `Workforce,Total Employees,${data.employees.total}\n`;
        csvContent += `Workforce,Active Employees,${data.employees.active}\n`;
        csvContent += `Workforce,On Leave,${data.employees.onLeave}\n`;
        csvContent += `Workforce,New Hires (30 days),${data.employees.newHires}\n`;
        csvContent += `Attendance,Present Today,${data.attendance.present}\n`;
        csvContent += `Attendance,Absent,${data.attendance.absent}\n`;
        csvContent += `Attendance,Late Arrivals,${data.attendance.late}\n`;
        csvContent += `Leave,Pending Requests,${data.leave.pending}\n`;
        csvContent += `Leave,Approved,${data.leave.approved}\n`;
        csvContent += `Leave,Rejected,${data.leave.rejected}\n`;
        csvContent += `Recruitment,Open Positions,${data.recruitment.openPositions}\n`;
        csvContent += `Recruitment,Applications,${data.recruitment.applications}\n`;
        csvContent += `Recruitment,Interviews,${data.recruitment.interviews}\n`;
        csvContent += `Recruitment,Hires,${data.recruitment.hires}\n`;
        break;
        
      case 'employee':
        csvContent = `Employee Report\nGenerated: ${timestamp}\n\n`;
        csvContent += `Metric,Value\n`;
        csvContent += `Total Employees,${data.employees.total}\n`;
        csvContent += `Active Employees,${data.employees.active}\n`;
        csvContent += `Currently On Leave,${data.employees.onLeave}\n`;
        csvContent += `New Hires (Last 30 Days),${data.employees.newHires}\n\n`;
        csvContent += `Department Distribution\n`;
        csvContent += `Department,Employee Count\n`;
        data.departments.forEach(dept => {
          csvContent += `${dept.name || 'Unassigned'},${dept.count || 0}\n`;
        });
        break;
        
      case 'attendance':
        csvContent = `Attendance Report\nGenerated: ${timestamp}\nDate Range: ${dateRange.from} to ${dateRange.to}\n\n`;
        csvContent += `Metric,Value\n`;
        csvContent += `Present Today,${data.attendance.present}\n`;
        csvContent += `Absent,${data.attendance.absent}\n`;
        csvContent += `Late Arrivals,${data.attendance.late}\n`;
        csvContent += `Average Working Hours,${data.attendance.avgHours}\n`;
        break;
        
      case 'leave':
        csvContent = `Leave Report\nGenerated: ${timestamp}\nDate Range: ${dateRange.from} to ${dateRange.to}\n\n`;
        csvContent += `Metric,Value\n`;
        csvContent += `Pending Requests,${data.leave.pending}\n`;
        csvContent += `Approved Requests,${data.leave.approved}\n`;
        csvContent += `Rejected Requests,${data.leave.rejected}\n`;
        csvContent += `Utilization Rate,${data.leave.utilizationRate}%\n`;
        break;
        
      case 'payroll':
        csvContent = `Payroll Report\nGenerated: ${timestamp}\n\n`;
        csvContent += `Metric,Value\n`;
        csvContent += `Total Paid,₦${data.payroll.totalPaid.toLocaleString()}\n`;
        csvContent += `Average Salary,₦${data.payroll.avgSalary.toLocaleString()}\n`;
        csvContent += `Pending Payments,${data.payroll.pendingPayments}\n`;
        break;
        
      case 'recruitment':
        csvContent = `Recruitment Report\nGenerated: ${timestamp}\n\n`;
        csvContent += `Metric,Value\n`;
        csvContent += `Open Positions,${data.recruitment.openPositions}\n`;
        csvContent += `Total Applications,${data.recruitment.applications}\n`;
        csvContent += `Scheduled Interviews,${data.recruitment.interviews}\n`;
        csvContent += `Total Hires,${data.recruitment.hires}\n`;
        break;
        
      case 'training':
        csvContent = `Training Report\nGenerated: ${timestamp}\n\n`;
        csvContent += `Metric,Value\n`;
        csvContent += `Active Courses,${data.training.activeCourses}\n`;
        csvContent += `Total Enrollments,${data.training.enrollments}\n`;
        csvContent += `Completion Rate,${data.training.completionRate}%\n`;
        break;
        
      case 'performance':
        csvContent = `Performance Report\nGenerated: ${timestamp}\n\n`;
        csvContent += `Metric,Value\n`;
        csvContent += `Average Rating,${data.performance.avgRating || 'N/A'}\n`;
        csvContent += `Reviews Completed,${data.performance.reviewsCompleted}\n`;
        csvContent += `Pending Reviews,${data.performance.pendingReviews}\n`;
        break;
        
      default:
        csvContent = `HR Report - ${type}\nGenerated: ${timestamp}\n\nNo data available`;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hr_${type}_report_${timestamp}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} report downloaded!`);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
      </div>
      {trend && (
        <div className="mt-3 flex items-center text-sm">
          <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
          <span className="text-green-600">{trend}</span>
        </div>
      )}
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: PieChart },
    { id: 'workforce', label: 'Workforce', icon: Users },
    { id: 'attendance', label: 'Attendance', icon: Clock },
    { id: 'leave', label: 'Leave', icon: Calendar },
    { id: 'payroll', label: 'Payroll', icon: DollarSign },
    { id: 'recruitment', label: 'Recruitment', icon: Briefcase },
    { id: 'training', label: 'Training', icon: GraduationCap },
    { id: 'performance', label: 'Performance', icon: Activity }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HR Reports & Analytics</h1>
          <p className="text-gray-500 mt-1">Comprehensive insights across all HR modules</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="border-none text-sm focus:ring-0"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="border-none text-sm focus:ring-0"
            />
          </div>
          <button
            onClick={() => exportReport('summary')}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="border-b overflow-x-auto">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="Total Employees" value={data.employees.total} icon={Users} color="text-blue-600" />
                <StatCard title="Pending Leaves" value={data.leave.pending} icon={Calendar} color="text-yellow-600" />
                <StatCard title="Open Positions" value={data.recruitment.openPositions} icon={Briefcase} color="text-purple-600" />
                <StatCard title="Avg Rating" value={data.performance.avgRating ? parseFloat(data.performance.avgRating).toFixed(1) : 'N/A'} icon={Activity} color="text-green-600" />
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Quick Metrics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span className="text-gray-600">Active Employees</span>
                      <span className="font-semibold text-blue-600">{data.employees.active}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span className="text-gray-600">On Leave Today</span>
                      <span className="font-semibold text-yellow-600">{data.employees.onLeave}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span className="text-gray-600">Applications Received</span>
                      <span className="font-semibold text-purple-600">{data.recruitment.applications}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span className="text-gray-600">Training Enrollments</span>
                      <span className="font-semibold text-green-600">{data.training.enrollments}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Department Distribution</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {data.departments.length > 0 ? data.departments.map((dept, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-gray-600">{dept.Department?.name || dept.name || 'Unassigned'}</span>
                        <span className="font-semibold text-blue-600">{dept.employee_count || dept.count || 0}</span>
                      </div>
                    )) : (
                      <p className="text-gray-500 text-center py-4">No department data</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Workforce Tab */}
          {activeTab === 'workforce' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={() => exportReport('employee')} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                  <Download className="h-4 w-4" /> Export Employee Report
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="Total Employees" value={data.employees.total} icon={Users} color="text-blue-600" />
                <StatCard title="Active Employees" value={data.employees.active} icon={UserCheck} color="text-green-600" />
                <StatCard title="On Leave" value={data.employees.onLeave} icon={Calendar} color="text-yellow-600" />
                <StatCard title="New Hires" value={data.employees.newHires} subtitle="Last 30 days" icon={Users} color="text-purple-600" />
              </div>
            </div>
          )}

          {/* Attendance Tab */}
          {activeTab === 'attendance' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={() => exportReport('attendance')} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                  <Download className="h-4 w-4" /> Export Attendance Report
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="Present Today" value={data.attendance.present} icon={UserCheck} color="text-green-600" />
                <StatCard title="Absent" value={data.attendance.absent} icon={Users} color="text-red-600" />
                <StatCard title="Late Arrivals" value={data.attendance.late} icon={Clock} color="text-yellow-600" />
                <StatCard title="Avg Hours" value={`${data.attendance.avgHours}h`} icon={Clock} color="text-blue-600" />
              </div>
            </div>
          )}

          {/* Leave Tab */}
          {activeTab === 'leave' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={() => exportReport('leave')} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                  <Download className="h-4 w-4" /> Export Leave Report
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="Pending Requests" value={data.leave.pending} icon={Clock} color="text-yellow-600" />
                <StatCard title="Approved" value={data.leave.approved} icon={UserCheck} color="text-green-600" />
                <StatCard title="Rejected" value={data.leave.rejected} icon={Users} color="text-red-600" />
                <StatCard title="Utilization Rate" value={`${data.leave.utilizationRate}%`} icon={PieChart} color="text-blue-600" />
              </div>
            </div>
          )}

          {/* Payroll Tab */}
          {activeTab === 'payroll' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={() => exportReport('payroll')} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                  <Download className="h-4 w-4" /> Export Payroll Report
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard title="Total Paid" value={`₦${(data.payroll.totalPaid || 0).toLocaleString()}`} icon={DollarSign} color="text-green-600" />
                <StatCard title="Avg Salary" value={`₦${(data.payroll.avgSalary || 0).toLocaleString()}`} icon={DollarSign} color="text-blue-600" />
                <StatCard title="Pending Payments" value={data.payroll.pendingPayments} icon={Clock} color="text-yellow-600" />
              </div>
            </div>
          )}

          {/* Recruitment Tab */}
          {activeTab === 'recruitment' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={() => exportReport('recruitment')} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                  <Download className="h-4 w-4" /> Export Recruitment Report
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="Open Positions" value={data.recruitment.openPositions} icon={Briefcase} color="text-blue-600" />
                <StatCard title="Applications" value={data.recruitment.applications} icon={FileText} color="text-purple-600" />
                <StatCard title="Interviews" value={data.recruitment.interviews} icon={Users} color="text-yellow-600" />
                <StatCard title="Hires" value={data.recruitment.hires} icon={UserCheck} color="text-green-600" />
              </div>
            </div>
          )}

          {/* Training Tab */}
          {activeTab === 'training' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={() => exportReport('training')} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                  <Download className="h-4 w-4" /> Export Training Report
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard title="Active Courses" value={data.training.activeCourses} icon={GraduationCap} color="text-blue-600" />
                <StatCard title="Enrollments" value={data.training.enrollments} icon={Users} color="text-purple-600" />
                <StatCard title="Completion Rate" value={`${data.training.completionRate}%`} icon={Activity} color="text-green-600" />
              </div>
            </div>
          )}

          {/* Performance Tab */}
          {activeTab === 'performance' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={() => exportReport('performance')} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                  <Download className="h-4 w-4" /> Export Performance Report
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard title="Avg Rating" value={data.performance.avgRating ? parseFloat(data.performance.avgRating).toFixed(1) : 'N/A'} icon={Activity} color="text-green-600" />
                <StatCard title="Reviews Completed" value={data.performance.reviewsCompleted} icon={FileText} color="text-blue-600" />
                <StatCard title="Pending Reviews" value={data.performance.pendingReviews} icon={Clock} color="text-yellow-600" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Report Generation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Quick Export Reports</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {[
            { type: 'summary', label: 'Summary', icon: FileText, color: 'gray' },
            { type: 'employee', label: 'Employees', icon: Users },
            { type: 'attendance', label: 'Attendance', icon: Clock },
            { type: 'leave', label: 'Leave', icon: Calendar },
            { type: 'payroll', label: 'Payroll', icon: DollarSign },
            { type: 'recruitment', label: 'Recruitment', icon: Briefcase },
            { type: 'training', label: 'Training', icon: GraduationCap },
            { type: 'performance', label: 'Performance', icon: Activity }
          ].map((report) => (
            <button
              key={report.type}
              onClick={() => exportReport(report.type)}
              className="flex flex-col items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-300 transition-colors"
            >
              <report.icon className="h-5 w-5 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">{report.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportsAnalytics;
